import { useRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DOCUMENT_PASSPORT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { RootState } from "app/lib/store/store";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";

import Loader from "app/components/common/Loader";
import UploadIcon from "app/assets/icons/UploadIcon";
import DeleteIcon from "app/assets/icons/DeleteIcon";
import UploadCertificateMedicareCard from "./UploadCertificateMedicareCard";
import pdf_icon from "app/assets/icons/pdf_icon.svg";
import HasValidAustralianPassport from "app/components/common/inputs/HasValidAustralianPassport";
import { handleApiError } from "app/utils/handleApiError";
import { createFileChangeHandler } from "app/utils/fileChangeHandler";

interface FormData {
  passportImage: File | null;
}

const PartFive: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isValidPassport, setIsValidPassport] = useState<boolean>(false);
  const [preview, setPreview] = useState<string | null>(null);
  const { passportImage, IsvalidPassport, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  );
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const { mutate: uploadS3 } = useS3Upload();
  const { mutate: uploadImages } = useAddAssetsLiabilitiesImages();
  const { applicantID } = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    watch,
    clearErrors,
    trigger,
  } = useForm<FormData>({ mode: "onChange" });
  const selectedFile = watch("passportImage");

  const onSubmit = async (data: FormData) => {
    if (!applicantID) {
      toast.error("Applicant id is required");
      return;
    }

    const { passportImage } = data;
    if (!(passportImage instanceof File)) {
      dispatch(nextStep());
      return;
    }
    setIsLoading(true);
    getPresignedUrls(
      {
        url: `/document/${applicantID}/presigned-urls`,
        data: { documentTypes: ["passport"] },
      },
      {
        onSuccess: (presignedData) => {
          const key = presignedData?.passport?.key;
          const uploadUrl = presignedData?.passport?.url;

          if (!key || !uploadUrl) {
            setIsLoading(false);
            toast.error("Failed to get pre-signed URL for passport image.");
            return;
          }
          uploadS3(
            {
              url: uploadUrl,
              data: passportImage,
            },
            {
              onSuccess: (data: any) => {
                if (data === 200) {
                  const file = {
                    passport: {
                      key: key,
                      fileName: passportImage.name,
                      mimeType: passportImage.type,
                    },
                  };
                  uploadImages(
                    {
                      url: `${DOCUMENT_PASSPORT}/${applicantID}/passport`,
                      data: file,
                    },
                    {
                      onSuccess: (data: any) => {
                        dispatch(
                          addStepsData({
                            ...others,
                            passportImage: data?.url,
                            IsvalidPassport: isValidPassport,
                          })
                        );
                        setIsLoading(false);
                        dispatch(nextStep());
                      },
                      onError: (error: any) => {
                        setIsLoading(false);
                        handleApiError(error);
                      },
                    }
                  );
                }
              },
            }
          );
        },
      }
    );
  };

  const handleFileChooseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePassportChange = createFileChangeHandler({
    fieldName: "passportImage",
    setValue,
    setError,
    clearErrors,
    trigger,
    setPreview,
  });

  // const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0] ?? null;

  //   clearErrors("passportImage");
  //   setPreview(null);

  //   if (!file) return;
  //   // Validate file size
  //   if (file.size > 5 * 1024 * 1024) {
  //     setError("passportImage", {
  //       type: "fileSize",
  //       message: "File size should not exceed 5MB",
  //     });
  //     e.target.value = "";
  //     return;
  //   }
  //   // Validate file type
  //   if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
  //     setError("passportImage", {
  //       type: "fileType",
  //       message: "Invalid file type. Only JPG, PNG are allowed",
  //     });
  //     e.target.value = "";
  //     return;
  //   }

  //   setValue("passportImage", file, { shouldValidate: true });
  //   await trigger("passportImage");
  // };

  useEffect(() => {
    if (selectedFile instanceof File) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (passportImage) {
      setPreview(passportImage);
    }
  }, [selectedFile, passportImage]);

  const handleDeleteImage = (type: "passportImage") => {
    if (type === "passportImage") {
      dispatch(
        addStepsData({
          ...others,
          passportImage: "",
        })
      );
      setPreview(null);
      setValue("passportImage", null);
    }
  };

  useEffect(() => {
    if (IsvalidPassport !== undefined) {
      setIsValidPassport(IsvalidPassport);
    }
  }, [IsvalidPassport]);

  const handleValidAustralianPassport = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    clearErrors("passportImage");
  };

  return (
    <>
      {isLoading && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <ProgressBar progress={60} MB={7} />
      <div
        className={`w-full sm:w-[80%] ${
          isValidPassport ? "xl:w-[60%]" : "xl:w-[40%]"
        }   mx-auto text-center px-4 sm:px-0`}
      >
        <MainTitle title="Legal Documents" />
        <SubTitle title="Please upload passport image" />
        <div className="text-left">
          <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-2">
            Upload passport image<span className="text-danger">*</span>
          </h6>
          <HasValidAustralianPassport
            isChecked={isValidPassport}
            passportImage={passportImage}
            selectedFile={selectedFile}
            setIsChecked={setIsValidPassport}
            onChange={(e) => handleValidAustralianPassport(e)}
          />
        </div>

        {isValidPassport ? (
          <UploadCertificateMedicareCard hasValidPassport={isValidPassport} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="text-left">
              {preview ? (
                <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[225px] flex justify-center items-center">
                  {selectedFile && selectedFile.type === "application/pdf" ? (
                    <div className="text-center">
                      <img
                        src={pdf_icon}
                        alt="pdf_icon "
                        className="rounded-[20px] text-center m-auto"
                      />
                      <p className="font-Mulish text-[#000] font-normal text-base w-[80%] overflow-hidden mx-auto">
                        {selectedFile.name}
                      </p>
                    </div>
                  ) : (
                    <img
                      src={preview}
                      alt="Uploaded Preview"
                      className="mx-auto  h-[200px]  rounded-[20px]"
                      onError={(e) => (e.currentTarget.src = pdf_icon)}
                    />
                  )}
                  <DeleteIcon
                    onClick={() => handleDeleteImage("passportImage")}
                    className="absolute cursor-pointer top-[10px] right-[10px]"
                  />
                </div>
              ) : (
                <div className="bg-[#EAECF0] py-5 w-full text-center relative  rounded-[20px] h-[225px]">
                  <div className="text-center mx-auto">
                    <UploadIcon />
                  </div>

                  <h6 className="text-[14px] font-normal text-darkgrey mb-3 mt-4">
                    Passport image
                  </h6>
                  <p className="text-[12px] font-normal text-[#98A2B3] w-[90%] sm:w-[60%] mx-auto mb-5">
                    Upload the front side of the document supports: JPG, PNG,
                    PDF
                  </p>
                  <div className="m-auto">
                    <input
                      type="file"
                      accept="image/jpeg, image/png, application/pdf"
                      {...register("passportImage", {
                        required: !passportImage
                          ? "Passport image is required"
                          : false,
                      })}
                      // onChange={handleFileChange}
                      onChange={handlePassportChange}
                      ref={fileInputRef}
                      hidden
                    />
                    <button
                      type="button"
                      onClick={handleFileChooseClick}
                      className="border border-solid border-[#ccc] text-sm font-normal  text-[#585C7B]  rounded-full px-4 py-2"
                    >
                      Choose file
                    </button>
                  </div>
                  {errors.passportImage && (
                    <ErrorsMessage
                      title={errors.passportImage.message}
                      className=" absolute top-[100%] left-0"
                    />
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between my-10">
              <PreviousButtom onClick={() => dispatch(prevStep())}>
                Previous
              </PreviousButtom>
              <ContinueButton type="submit" loading={isLoading}>
                Next
              </ContinueButton>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default PartFive;
